import {
  PropsWithChildren,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  FloatingDelayGroup,
  FloatingPortal,
  type Placement,
  autoUpdate,
  flip,
  offset as offsetMiddleware,
  shift,
  useClientPoint,
  useDelayGroup,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { motion, useSpring } from 'framer-motion';
import { FlattenSimpleInterpolation } from 'styled-components';
import { Colors } from 'styles';
import { parseTrigger } from '../parseTrigger';

const springConfig = {
  damping: 80,
  stiffness: 800,
} as const;

const INITIAL_SCALE = 0.85;

// Default 10px gap between the reference and the tooltip. Call sites that omit `offset` rely on
// this distance, so it is preserved to keep placement unchanged.
const DEFAULT_OFFSET: [number, number] = [0, 10];

const MotionDiv = motion.div;

type GroupDelay = number | { open?: number; close?: number };

export interface TooltipProps {
  content?: React.ReactNode;
  placement?: Placement;
  trigger?: string;
  hideOnClick?: boolean;
  // [skidding, distance] tuple — preserves the existing offset shape so call sites are unchanged.
  offset?: [number, number];
  // Controlled visibility. When provided, hover/focus triggers are disabled.
  visible?: boolean;
  followCursor?: boolean;
  // Open/close hover delay in ms. Either a number, or a [show, hide] tuple.
  delay?: number | [number, number];
  disabled?: boolean;
  // Called when the tooltip is about to show; returning `false` cancels it.
  onShow?: (instance: { reference: Element }) => void | false;
  onTrigger?: () => void;
  onUntrigger?: () => void;
  // Accepted for backwards compatibility; the wrapper has never rendered an arrow, so this
  // remains a no-op.
  arrow?: boolean;
  // Accepted for backwards compatibility; the tooltip always renders in a
  // FloatingPortal at the document body (matching the previous z-index intent).
  appendTo?: unknown;
  backgroundColor?: string;
  color?: string;
  centerContent?: boolean;
  contentCss?: FlattenSimpleInterpolation;
}

/**
 * Wrap a set of tooltips so they share open/close delay and transition instantly between
 * siblings, so moving between them does not replay the open delay.
 */
export function TooltipGroup({
  children,
  delay = { open: 0, close: 100 },
}: PropsWithChildren<{ delay?: GroupDelay }>): React.ReactElement {
  return <FloatingDelayGroup delay={delay}>{children}</FloatingDelayGroup>;
}

function Tooltip({
  children,
  content,
  contentCss,
  backgroundColor = Colors.gray7,
  color = Colors.gray1,
  centerContent = false,
  hideOnClick = true,
  placement = 'top',
  trigger,
  offset = DEFAULT_OFFSET,
  visible,
  followCursor = false,
  delay: delayProp,
  disabled = false,
  onShow,
  onTrigger,
  onUntrigger,
}: PropsWithChildren<TooltipProps>): React.ReactElement {
  const isControlled = visible !== undefined;
  const hasContent = content != null;
  const enabled = !disabled && hasContent;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? Boolean(visible) : internalOpen;

  // When the tooltip becomes disabled or loses its content while open, the
  // hover/focus close handlers are no longer active, so force it closed —
  // otherwise a stale `internalOpen` would re-show it if content later returns
  // without a fresh hover.
  useEffect(() => {
    if (!isControlled && !enabled && internalOpen) setInternalOpen(false);
  }, [isControlled, enabled, internalOpen]);

  const opacity = useSpring(0, springConfig);
  const scale = useSpring(INITIAL_SCALE, springConfig);

  const [skidding, distance] = offset;
  const referenceRef = useRef<Element | null>(null);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      onTrigger?.();
      if (referenceRef.current != null && onShow?.({ reference: referenceRef.current }) === false) {
        return;
      }
    } else {
      onUntrigger?.();
    }
    setInternalOpen(next);
  };

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: handleOpenChange,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offsetMiddleware({ mainAxis: distance, crossAxis: skidding }),
      flip(),
      shift({ padding: 8 }),
    ],
  });

  // Use the delay-group timing once a grouped tooltip is open (instant
  // sibling transitions); otherwise honor this tooltip's own delay prop.
  const group = useDelayGroup(context);
  const propDelay = Array.isArray(delayProp)
    ? { open: delayProp[0], close: delayProp[1] }
    : delayProp;
  const effectiveDelay = group.currentId != null ? group.delay : propDelay;

  // A tooltip treats mousemove as a hover trigger (cursor-following), so fold it into hover.
  const { mouseenter, mousemove, focus: focusTrigger } = parseTrigger(trigger);
  const hoverTrigger = mouseenter || mousemove;

  const hover = useHover(context, {
    enabled: !isControlled && enabled && hoverTrigger,
    move: mousemove,
    delay: effectiveDelay,
  });
  const focus = useFocus(context, { enabled: !isControlled && enabled && focusTrigger });
  const clientPoint = useClientPoint(context, { enabled: followCursor && enabled });
  const dismiss = useDismiss(context, { enabled: !isControlled, referencePress: hideOnClick });
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    clientPoint,
    dismiss,
    role,
  ]);

  // Rendering is gated on `open` directly (no deferred mount state), so the
  // floating element unmounts synchronously on close — keeping React state
  // updates inside the triggering act() and avoiding stuck/leaked roots. The
  // entrance fade/scale is driven by the springs (motion values, not React
  // state, so they don't require act()).
  useEffect(() => {
    if (open) {
      scale.set(1);
      opacity.set(1);
    } else {
      scale.set(INITIAL_SCALE);
      opacity.set(0);
    }
  }, [open, scale, opacity]);

  const childRef = useMergeRefs([refs.setReference, referenceRef, (children as any)?.ref ?? null]);
  const referenceChild = isValidElement(children)
    ? cloneElement(children as any, {
        ref: childRef,
        ...getReferenceProps((children as any).props),
      })
    : children;

  return (
    <>
      {referenceChild}
      {open && enabled ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 10000000000,
              pointerEvents: followCursor ? 'none' : undefined,
            }}
            {...getFloatingProps()}
          >
            <MotionDiv
              data-testid="tooltip"
              style={{ opacity, scale }}
              css={`
                padding: 0.3rem 0.6rem;
                font-weight: 500;
                border-radius: 0.5rem;
                font-size: 1.2rem;
                background-color: ${backgroundColor};
                color: ${color};
                text-align: ${centerContent ? 'center' : 'inherit'};
                ${contentCss ? ` ${contentCss}` : ''}
              `}
            >
              {content}
            </MotionDiv>
          </div>
        </FloatingPortal>
      ) : null}
    </>
  );
}

export default Tooltip;
